import React from "react";
import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import Table, { Row, Cell, NoDataCell } from "../../ui/Table";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { selectAdvancedMode } from "../settings/slice";
import Timestamp from "../../ui/Timestamp";
import IDCopy from "../../ui/IDCopy";
import AstrositeName from "../astrosites/AstrositeName";
import { useNavigate } from "react-router-dom";
import DropDownFilter from "../filters/DropDownFilter";
import SortHeader from "../sorting/SortHeader";
import { selectAstrositesById } from "../astrosites/slice";
import DateTimeFilter from "../filters/DateTimeFilter";

/**
 * Renders a list of playlists matching the filters
 *
 * @param timelapses
 * @param filter
 * @param setFilter
 * @param stickyHeader
 * @returns {JSX.Element}
 * @constructor
 */
const PlaylistsTable = ({
	playlists = [],
	filter = {},
	setFilter = () => {},
	stickyHeader = true,
	hideFilters = false,
}) => {
	const advancedMode = useSelector(selectAdvancedMode);
	const astrositesByIds = useSelector(selectAstrositesById);

	const navigate = useNavigate();
	const navigateTo = (taskListId) => () => navigate(`/playlists/${taskListId}`);

	return (
		<Table>
			<TableHeaderRow sticky={stickyHeader}>
				{advancedMode && <TableHeader>ID</TableHeader>}
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={Object.keys(astrositesByIds).map((a) => ({
									name: a,
									value: a,
								}))}
								filterByName="astrosite_id"
								filter={filter}
								setFilter={setFilter}
								active={filter.astrosite_id ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"astrosite_id"}
							/>
						)) ||
						undefined
					}
				>
					Astrosite
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DateTimeFilter
								filter={filter}
								setFilter={setFilter}
								active={filter.from || filter.to ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"created_at"}
							/>
						)) ||
						undefined
					}
				>
					Created At
				</TableHeader>
				<TableHeader>Scheduler status</TableHeader>
				<TableHeader>Deployment status</TableHeader>
				<TableHeader>By</TableHeader>
				<TableHeader>Tasks</TableHeader>
			</TableHeaderRow>
			{playlists && (
				<tbody>
					{playlists.map((playlist) => (
						<Row key={playlist.id} zebra>
							{advancedMode && (
								<Cell onClick={navigateTo(playlist.id)}>
									<IDCopy truncateTo={20} id={playlist.id} showCopy={true} />
								</Cell>
							)}
							<Cell onClick={navigateTo(playlist.id)}>
								<AstrositeName
									astrositeId={playlist.astrosite_id}
									showLinkIcon={false}
								/>
							</Cell>
							<Cell onClick={navigateTo(playlist.id)}>
								<Timestamp utc={advancedMode} columnMode={true}>
									{playlist.created_at}
								</Timestamp>
							</Cell>
							<Cell onClick={navigateTo(playlist.id)}>
								{playlist.scheduler_status}
							</Cell>
							<Cell onClick={navigateTo(playlist.id)}>
								{playlist.deployment_status}
							</Cell>
							<Cell onClick={navigateTo(playlist.id)}>
								{playlist.created_by || "-"}
							</Cell>
							<Cell onClick={navigateTo(playlist.id)}>
								{/* We divide by 2 as a hack because there is always a idle task between each task*/}
								{playlist?.task_count && playlist.task_count != 0
									? playlist.task_count / 2
									: "0"}
							</Cell>
						</Row>
					))}
				</tbody>
			)}
			{playlists != null && playlists.length === 0 && (
				<Row>
					<NoDataCell colSpan="100%">No matching data</NoDataCell>
				</Row>
			)}
		</Table>
	);
};

PlaylistsTable.propTypes = {
	playlists: PropTypes.array, // the playlists to render
	filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
	setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
	stickyHeader: PropTypes.bool, // If true, table header is sticky
	hideFilters: PropTypes.bool, // If provided hides the filters for the table
};

export default PlaylistsTable;
